/**
 * Spreadsheet → CSV, in the browser.
 *
 * ExecBridge's metrics import endpoint accepts CSV only, so XLSX is converted
 * client-side before upload — the same approach the reference app takes in
 * `phase1/import/parse-sheet.ts`. Keeping this out of the component makes it
 * unit-testable; see `sheet-to-csv.test.ts`.
 */

/**
 * Excel cell → string, matching the reference's `formatCellValue`.
 *
 * Date cells are the reason this exists: ExecBridge validates
 * `measurement_date` strictly as YYYY-MM-DD, and SheetJS otherwise yields
 * either an Excel serial (45945) or a locale-formatted string, both of which
 * fail. UTC components are used so a date never drifts a day by timezone.
 */
export function formatCellValue(v: unknown): string {
  if (v == null) return "";
  if (v instanceof Date) {
    const y = v.getUTCFullYear();
    const m = String(v.getUTCMonth() + 1).padStart(2, "0");
    const d = String(v.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return String(v).trim();
}

function isRealDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const probe = new Date(Date.UTC(y, m - 1, d));
  return (
    probe.getUTCFullYear() === y &&
    probe.getUTCMonth() === m - 1 &&
    probe.getUTCDate() === d
  );
}

function build(y: number, m: number, d: number): string | null {
  if (!isRealDate(y, m, d)) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(
    d
  ).padStart(2, "0")}`;
}

/**
 * Text date → YYYY-MM-DD, or null when it isn't a date after all.
 *
 * `cellDates` only helps when Excel stored a real date; a column typed as Text
 * arrives as "1/15/2026" and would still be rejected. This mirrors the accepted
 * forms in `services/datahub/core/qc_engine.py::try_parse_date` so the client
 * and the QC engine agree on what counts as a date. Ambiguous numeric forms are
 * read US-style (M/D/Y), matching that function.
 */
export function parseDateish(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (iso) return isRealDate(+iso[1], +iso[2], +iso[3]) ? t : null;

  const ymd = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(t);
  if (ymd) return build(+ymd[1], +ymd[2], +ymd[3]);

  const mdy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/.exec(t);
  if (mdy) {
    let y = +mdy[3];
    if (mdy[3].length === 2) y = y > 50 ? 1900 + y : 2000 + y;
    return build(y, +mdy[1], +mdy[2]);
  }

  // Trailing time component, e.g. "2026-01-15T00:00:00" or "2026-01-15 00:00:00".
  const withTime = /^(\d{4}-\d{2}-\d{2})[T ]\d{2}:\d{2}/.exec(t);
  if (withTime) return parseDateish(withTime[1]);

  return null;
}

/** Columns whose text values get run through `parseDateish`. */
export function isDateHeader(header: string): boolean {
  return /date/i.test(header);
}

const DOUBLE_QUOTE = String.fromCharCode(34);

/** RFC-4180 quoting so commas/quotes in cells don't corrupt the CSV. */
export function csvCell(s: string): string {
  const doubled = s.replace(/"/g, DOUBLE_QUOTE.repeat(2));
  return /[",\n\r]/.test(s) ? `${DOUBLE_QUOTE}${doubled}${DOUBLE_QUOTE}` : s;
}

/** Rows-of-cells → CSV text, applying date normalization to date columns. */
export function rowsToCsv(aoa: unknown[][]): string {
  // Which columns to date-normalize, decided once from the header row.
  const dateCols = (aoa[0] ?? []).map(h => isDateHeader(formatCellValue(h)));

  return aoa
    .map((row, rowIdx) =>
      (row ?? [])
        .map((v, colIdx) => {
          const text = formatCellValue(v);
          // `formatCellValue` already emits ISO for real Date cells; this only
          // rescues date columns Excel stored as text.
          if (rowIdx > 0 && dateCols[colIdx] && text) {
            return csvCell(parseDateish(text) ?? text);
          }
          return csvCell(text);
        })
        .join(",")
    )
    .filter(line => line.replace(/,/g, "").trim() !== "")
    .join("\n");
}

/**
 * A parsed sheet, the File step's output and every later step's input.
 * Mirrors `phase1/import/parse-sheet.ts::SheetTable`.
 */
export type SheetTable = {
  headers: string[];
  /** Row objects keyed by header; blanks are "" so callers never see undefined. */
  rows: Record<string, string>[];
  /** Sheet name for XLSX, file name for CSV. */
  sheet_name: string;
  /** Every sheet in the workbook; single-element for CSV. */
  all_sheet_names: string[];
  /**
   * Repeated columns that were left out because they added nothing: every row
   * was empty in them or identical to the first column of that name.
   */
  repeated_columns_dropped?: string[];
};

/** Split one CSV line, honouring RFC-4180 quoting. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === DOUBLE_QUOTE) {
        if (line[i + 1] === DOUBLE_QUOTE) {
          cur += DOUBLE_QUOTE;
          i++;
        } else quoted = false;
      } else cur += ch;
    } else if (ch === DOUBLE_QUOTE) {
      quoted = true;
    } else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

/**
 * Give repeated header names a distinct suffix: "Formula", "Formula (2)".
 *
 * Rows are keyed by header, so two columns both called "Formula" collapsed
 * into one — the second silently overwrote the first in every row — and the
 * Columns step, keyed the same way, changed both mappings at once and refused
 * to continue. A suffixed copy matches no known field, so it lands on
 * "Do not import" by itself and the user can still map it if it is the one
 * that holds data. Blank headers are left as they are.
 */
export function uniqueHeaders(headers: string[]): string[] {
  const seen: Record<string, number> = {};
  const taken: Record<string, boolean> = {};
  headers.forEach(h => {
    if (h) taken[h] = true;
  });
  return headers.map(h => {
    if (!h) return h;
    seen[h] = (seen[h] ?? 0) + 1;
    if (seen[h] === 1) return h;
    let n = seen[h];
    let candidate = `${h} (${n})`;
    while (taken[candidate]) {
      n += 1;
      candidate = `${h} (${n})`;
    }
    taken[candidate] = true;
    return candidate;
  });
}

/** Rows-of-cells → header + row objects, with date columns normalized. */
export function tableFromRows(
  aoa: unknown[][],
  sheetName: string,
  allSheetNames: string[]
): SheetTable {
  const headerRow = (aoa[0] ?? []).map(h => formatCellValue(h));
  // Blank trailing headers are common in exported sheets; drop them so they
  // don't show up as unmappable columns.
  const lastNamed = headerRow.reduce((last, h, i) => (h ? i : last), -1);
  const headers = uniqueHeaders(headerRow.slice(0, lastNamed + 1));
  const dateCols = headers.map(h => isDateHeader(h));

  const rows: Record<string, string>[] = [];
  for (let r = 1; r < aoa.length; r++) {
    const raw = aoa[r] ?? [];
    const obj: Record<string, string> = {};
    let any = false;
    headers.forEach((h, c) => {
      const text = formatCellValue(raw[c]);
      const value = dateCols[c] && text ? (parseDateish(text) ?? text) : text;
      obj[h] = value;
      if (value) any = true;
    });
    if (any) rows.push(obj);
  }

  // A file can carry the same column twice (the customer's export repeats
  // Formula, Source System and Source Table). A repeat that is empty, or the
  // same as the first copy in every row, is a true duplicate: leave it out so
  // it never reaches the Columns step. One that holds different data stays,
  // suffixed "(2)", because deleting it could lose the only real values.
  const original = headerRow.slice(0, lastNamed + 1);
  const firstIndex: Record<string, number> = {};
  const redundant: Record<number, boolean> = {};
  original.forEach((name, c) => {
    if (!name) return;
    if (firstIndex[name] === undefined) {
      firstIndex[name] = c;
      return;
    }
    const first = headers[firstIndex[name]];
    const copy = headers[c];
    redundant[c] = rows.every(r => !r[copy] || r[copy] === r[first]);
  });
  const dropped = original.filter((_, c) => redundant[c]);
  if (dropped.length === 0) {
    return {
      headers,
      rows,
      sheet_name: sheetName,
      all_sheet_names: allSheetNames,
    };
  }
  const keep = headers.filter((_, c) => !redundant[c]);
  const drop = headers.filter((_, c) => redundant[c]);
  return {
    headers: keep,
    rows: rows.map(r => {
      const out = { ...r };
      drop.forEach(h => {
        delete out[h];
      });
      return out;
    }),
    sheet_name: sheetName,
    all_sheet_names: allSheetNames,
    repeated_columns_dropped: dropped,
  };
}

/**
 * Parse a CSV or XLSX file into a `SheetTable`.
 *
 * This is what the wizard's File step produces; Columns/Accounts/Metrics all
 * read the result rather than re-reading the file.
 */
export async function parseSheet(
  file: File,
  opts: { sheetName?: string } = {}
): Promise<SheetTable> {
  const isCsv =
    file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

  if (isCsv) {
    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .filter(l => l.replace(/,/g, "").trim() !== "");
    if (lines.length === 0) throw new Error("That file is empty.");
    return tableFromRows(lines.map(splitCsvLine), file.name, [file.name]);
  }

  const XLSX = await import("xlsx");
  const wb = XLSX.read(await file.arrayBuffer(), {
    type: "array",
    cellDates: true,
  });
  if (wb.SheetNames.length === 0)
    throw new Error("That workbook has no sheets.");
  const sheetName =
    opts.sheetName && wb.SheetNames.includes(opts.sheetName)
      ? opts.sheetName
      : wb.SheetNames[0];

  const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], {
    header: 1,
    defval: "",
    blankrows: false,
    raw: true,
  });
  if (aoa.length === 0) throw new Error(`Sheet "${sheetName}" is empty.`);
  return tableFromRows(aoa, sheetName, wb.SheetNames);
}

/**
 * A parsed table back to CSV, for the CSV-only import endpoint.
 *
 * With `mappings`, headers are rewritten to the canonical names the importer
 * expects and ignored columns are dropped — so what the user decided in the
 * Columns step is what actually gets uploaded, rather than relying on the
 * server guessing from the original spellings.
 */
export function tableToCsv(
  table: SheetTable,
  mappings?: { header: string; target: string }[]
): string {
  const cols = mappings
    ? mappings.filter(m => m.target !== "ignore")
    : table.headers.map(h => ({ header: h, target: h }));

  const lines = [cols.map(c => csvCell(c.target)).join(",")];
  table.rows.forEach(r => {
    lines.push(cols.map(c => csvCell(r[c.header] ?? "")).join(","));
  });
  return lines.join("\n");
}

/**
 * Convert a spreadsheet to CSV so the existing CSV import endpoint can consume
 * it. CSV files pass through untouched.
 */
export async function toCsvFile(file: File): Promise<File> {
  const isCsv =
    file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
  if (isCsv) return file;

  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  // cellDates: true → real Date objects instead of raw Excel serials.
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error("That workbook has no sheets.");

  const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], {
    header: 1,
    defval: "",
    blankrows: false,
    raw: true,
  });
  if (aoa.length === 0) throw new Error("That sheet is empty.");

  const base = file.name.replace(/\.[^.]+$/, "");
  return new File([rowsToCsv(aoa)], `${base}.csv`, { type: "text/csv" });
}

/** Sheet names, for the "which sheet will be used" note in the wizard. */
export async function readSheetNames(file: File): Promise<string[]> {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(await file.arrayBuffer(), {
    type: "array",
    cellDates: true,
  });
  return wb.SheetNames;
}
